import * as monacoEditor from 'monaco-editor';
import { Diagnostic, DiagnosticSeverity, WorkspaceEdit } from 'vscode-languageserver-protocol';
import type { CodeAction, Command } from 'vscode-languageserver-protocol';
import { TextDocumentEdit } from 'vscode-languageserver-types';
import { LSP_DOCUMENT_URI, LspClient } from '../LspClient';
import { buildAddMissingImportsEdits, buildMissingImportQuickFixes, buildOrganizeImportsFallbackEdits } from './service';
import { DiagnosticLike, OffsetTextEdit, WorkspaceModuleLike } from './types';

const QUICK_FIX_KIND = 'quickfix';
const SOURCE_ORGANIZE_IMPORTS_KIND = 'source.organizeImports';
let codeActionProviderRegistered = false;

function markerCodeToDiagnosticCode(code: monacoEditor.editor.IMarkerData['code']): Diagnostic['code'] {
  if (typeof code === 'string' || typeof code === 'number') {
    return code;
  }

  if (code && typeof code === 'object' && 'value' in code) {
    return code.value;
  }

  return undefined;
}

function markerSeverityToDiagnosticSeverity(severity: monacoEditor.MarkerSeverity): DiagnosticSeverity | undefined {
  switch (severity) {
    case monacoEditor.MarkerSeverity.Error:
      return DiagnosticSeverity.Error;
    case monacoEditor.MarkerSeverity.Warning:
      return DiagnosticSeverity.Warning;
    case monacoEditor.MarkerSeverity.Info:
      return DiagnosticSeverity.Information;
    case monacoEditor.MarkerSeverity.Hint:
      return DiagnosticSeverity.Hint;
    default:
      return undefined;
  }
}

function markerToDiagnosticLike(model: monacoEditor.editor.ITextModel, marker: monacoEditor.editor.IMarkerData): DiagnosticLike {
  return {
    message: marker.message,
    code: marker.code,
    startOffset: model.getOffsetAt({
      lineNumber: marker.startLineNumber,
      column: marker.startColumn,
    }),
    endOffset: model.getOffsetAt({
      lineNumber: marker.endLineNumber,
      column: marker.endColumn,
    }),
    tags: marker.tags,
  };
}

function markerToLspDiagnostic(marker: monacoEditor.editor.IMarkerData): Diagnostic {
  return {
    message: marker.message,
    code: markerCodeToDiagnosticCode(marker.code),
    source: typeof marker.source === 'string' ? marker.source : undefined,
    severity: marker.severity ? markerSeverityToDiagnosticSeverity(marker.severity) : undefined,
    tags: marker.tags,
    range: {
      start: {
        line: marker.startLineNumber - 1,
        character: marker.startColumn - 1,
      },
      end: {
        line: marker.endLineNumber - 1,
        character: marker.endColumn - 1,
      },
    },
  };
}

function offsetEditsToWorkspaceTextEdits(
  model: monacoEditor.editor.ITextModel,
  edits: OffsetTextEdit[]
): monacoEditor.languages.IWorkspaceTextEdit[] {
  return edits.map((edit) => ({
    resource: model.uri,
    versionId: undefined,
    textEdit: {
      range: {
        startLineNumber: model.getPositionAt(edit.startOffset).lineNumber,
        startColumn: model.getPositionAt(edit.startOffset).column,
        endLineNumber: model.getPositionAt(edit.endOffset).lineNumber,
        endColumn: model.getPositionAt(edit.endOffset).column,
      },
      text: edit.newText,
    },
  }));
}

function workspaceEditToSingleModelEdits(
  model: monacoEditor.editor.ITextModel,
  workspaceEdit: WorkspaceEdit | null
): monacoEditor.languages.IWorkspaceTextEdit[] {
  if (!workspaceEdit) {
    return [];
  }

  // LSP returns edits keyed under the Pyright document URI, not the Monaco model URI.
  const edits: monacoEditor.languages.IWorkspaceTextEdit[] = [];

  for (const textEdit of workspaceEdit.changes?.[LSP_DOCUMENT_URI] ?? []) {
    edits.push({
      resource: model.uri,
      versionId: undefined,
      textEdit: {
        range: {
          startLineNumber: textEdit.range.start.line + 1,
          startColumn: textEdit.range.start.character + 1,
          endLineNumber: textEdit.range.end.line + 1,
          endColumn: textEdit.range.end.character + 1,
        },
        text: textEdit.newText,
      },
    });
  }

  for (const documentChange of workspaceEdit.documentChanges ?? []) {
    if (!TextDocumentEdit.is(documentChange) || documentChange.textDocument.uri !== LSP_DOCUMENT_URI) {
      continue;
    }

    for (const textEdit of documentChange.edits) {
      edits.push({
        resource: model.uri,
        versionId: undefined,
        textEdit: {
          range: {
            startLineNumber: textEdit.range.start.line + 1,
            startColumn: textEdit.range.start.character + 1,
            endLineNumber: textEdit.range.end.line + 1,
            endColumn: textEdit.range.end.character + 1,
          },
          text: textEdit.newText,
        },
      });
    }
  }

  return edits;
}

function buildWorkspaceEdit(model: monacoEditor.editor.ITextModel, edits: OffsetTextEdit[]): monacoEditor.languages.WorkspaceEdit | undefined {
  if (edits.length === 0) {
    return undefined;
  }

  return {
    edits: offsetEditsToWorkspaceTextEdits(model, edits),
  };
}

function getModelMarkers(monacoApi: typeof monacoEditor, model: monacoEditor.editor.ITextModel): monacoEditor.editor.IMarker[] {
  return monacoApi.editor.getModelMarkers({
    resource: model.uri,
    owner: 'pyright',
  });
}

function getModelDiagnostics(monacoApi: typeof monacoEditor, model: monacoEditor.editor.ITextModel): DiagnosticLike[] {
  return getModelMarkers(monacoApi, model).map((marker) => markerToDiagnosticLike(model, marker));
}

function isDirectCommand(codeAction: CodeAction | Command): codeAction is Command {
  return typeof codeAction.command === 'string';
}

function hasWorkspaceEdit(codeAction: CodeAction | Command): codeAction is CodeAction & { edit: WorkspaceEdit } {
  return 'edit' in codeAction && !!codeAction.edit;
}

function hasNestedCommand(codeAction: CodeAction | Command): codeAction is CodeAction & { command: Command } {
  return typeof codeAction.command === 'object' && codeAction.command !== null && typeof codeAction.command.command === 'string';
}

async function resolveOrganizeImportsWorkspaceEdit(
  code: string,
  model: monacoEditor.editor.ITextModel,
  lspClient: LspClient,
  diagnostics: Diagnostic[]
): Promise<WorkspaceEdit | null> {
  const codeActions = await lspClient.getCodeActions(code, model.getFullModelRange(), diagnostics, [SOURCE_ORGANIZE_IMPORTS_KIND]);
  if (!codeActions || codeActions.length === 0) {
    return null;
  }

  for (const codeAction of codeActions) {
    if (hasWorkspaceEdit(codeAction)) {
      return codeAction.edit;
    }

    if (isDirectCommand(codeAction)) {
      return lspClient.executeCommand(codeAction.command, codeAction.arguments ?? []);
    }

    if (hasNestedCommand(codeAction)) {
      return lspClient.executeCommand(codeAction.command.command, codeAction.command.arguments ?? []);
    }
  }

  return null;
}

export function buildPythonImportCodeActions(
  model: monacoEditor.editor.ITextModel,
  range: monacoEditor.Range,
  markers: readonly monacoEditor.editor.IMarkerData[],
  allDiagnostics: DiagnosticLike[],
  workspaceModules: WorkspaceModuleLike[] = []
): monacoEditor.languages.CodeAction[] {
  const quickFixes = buildMissingImportQuickFixes(
    model.getValue(),
    model.getOffsetAt(range.getStartPosition()),
    model.getOffsetAt(range.getEndPosition()),
    {
      diagnostics: allDiagnostics,
      workspaceModules,
    }
  );

  return quickFixes.map((quickFix) => ({
    title: quickFix.title,
    kind: QUICK_FIX_KIND,
    diagnostics: [...markers],
    isPreferred: quickFix.isPreferred,
    edit: buildWorkspaceEdit(model, quickFix.edits),
  }));
}

export function applyWorkspaceTextEdits(
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  edits: monacoEditor.languages.IWorkspaceTextEdit[]
): boolean {
  const model = editor.getModel();
  if (!model || edits.length === 0) {
    return false;
  }

  model.pushStackElement();
  model.pushEditOperations(
    null,
    edits.map((edit) => ({
      range: edit.textEdit.range,
      text: edit.textEdit.text,
    })),
    () => null
  );
  model.pushStackElement();
  return true;
}

export function applyOffsetEdits(editor: monacoEditor.editor.IStandaloneCodeEditor, edits: OffsetTextEdit[]): boolean {
  const model = editor.getModel();
  if (!model || edits.length === 0) {
    return false;
  }

  return applyWorkspaceTextEdits(editor, offsetEditsToWorkspaceTextEdits(model, edits));
}

export function registerPythonImportCodeActionProvider(monacoApi: typeof monacoEditor): monacoEditor.IDisposable | undefined {
  if (codeActionProviderRegistered) {
    return undefined;
  }

  codeActionProviderRegistered = true;
  return monacoApi.languages.registerCodeActionProvider(
    'python',
    {
      provideCodeActions(model, range, context) {
        const allDiagnostics = getModelDiagnostics(monacoApi, model);
        return {
          actions: buildPythonImportCodeActions(model, range, context.markers, allDiagnostics),
          dispose: () => {},
        };
      },
    },
    {
      providedCodeActionKinds: [QUICK_FIX_KIND],
    }
  );
}

export function registerPythonImportEditorActions(
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  monacoApi: typeof monacoEditor,
  lspClient: LspClient,
  workspaceModules: WorkspaceModuleLike[] = []
): monacoEditor.IDisposable[] {
  return [
    editor.addAction({
      id: 'python.addMissingImports',
      label: 'Add Missing Imports',
      keybindings: [monacoApi.KeyMod.CtrlCmd | monacoApi.KeyMod.Shift | monacoApi.KeyCode.KeyM],
      contextMenuGroupId: '1_modification',
      contextMenuOrder: 1.5,
      precondition: '!editorReadonly',
      run: async () => {
        const model = editor.getModel();
        if (!model) {
          return;
        }

        const edits = buildAddMissingImportsEdits(model.getValue(), {
          diagnostics: getModelDiagnostics(monacoApi, model),
          workspaceModules,
        });
        applyOffsetEdits(editor, edits);
      },
    }),
    editor.addAction({
      id: 'python.organizeImports',
      label: 'Organize Imports',
      keybindings: [monacoApi.KeyMod.Shift | monacoApi.KeyMod.Alt | monacoApi.KeyCode.KeyO],
      contextMenuGroupId: '1_modification',
      contextMenuOrder: 1.6,
      precondition: '!editorReadonly',
      run: async () => {
        const model = editor.getModel();
        if (!model) {
          return;
        }

        const code = model.getValue();
        const diagnostics = getModelMarkers(monacoApi, model).map((marker) => markerToLspDiagnostic(marker));
        const organizeImportsWorkspaceEdit = await resolveOrganizeImportsWorkspaceEdit(code, model, lspClient, diagnostics);
        const organizeImportsEdits = workspaceEditToSingleModelEdits(model, organizeImportsWorkspaceEdit);

        if (applyWorkspaceTextEdits(editor, organizeImportsEdits)) {
          return;
        }

        const fallbackEdits = buildOrganizeImportsFallbackEdits(code, {
          diagnostics: getModelDiagnostics(monacoApi, model),
          workspaceModules,
        });
        applyOffsetEdits(editor, fallbackEdits);
      },
    }),
  ];
}
